/**
 * LastCommentPreview — einzeilige Vorschau des letzten Staffing-Kommentars für eine
 * EINGEKLAPPTE Projektkarte (Staffing-Board und KFML-Worklist).
 *
 * Quelle ist immer ein bereits geladenes Feld der jeweiligen Sammel-Query
 * (`last_comment` in /kfml-worklist, `last_comments[0]` in /kl-board) — bewusst KEIN
 * Nachladen je Zeile.
 *
 * Layout-Entscheidung (Feedback 2026-07-21): Der KOMMENTARTEXT ist die Information, alles
 * andere ist Beiwerk. Deshalb steht der Text links, groß und in der Vordergrundfarbe; Autor
 * und Zeitstempel stehen klein und sekundär rechts am Rand und schrumpfen nicht in den
 * Textbereich hinein. Der Klammerzusatz im Anzeigenamen („Christoph (Kaufm. Leitung global)")
 * wird für die Randanzeige abgeschnitten — vollständig steht er im Tooltip.
 *
 * i18n: nutzt die bestehenden `v2.worklist.last_comment_*`-Keys, auch wenn die Komponente
 * jetzt in beiden Ansichten hängt — ein zweiter Satz identischer Texte wäre Doppelpflege.
 */
import type { StaffingComment } from '@projektverwaltung/shared';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api.js';
import { mentionBodyToPlainText } from '../mention-input.js';

/** „Christoph (Kaufm. Leitung global)" → „Christoph" — Rollenzusatz kostet nur Breite. */
function shortName(name: string): string {
  return name.replace(/\s*\(.*\)\s*$/, '');
}

export function LastCommentPreview({ comment }: { comment: StaffingComment }) {
  const { t, i18n } = useTranslation();
  const usersQuery = useQuery({ queryKey: ['users-all'], queryFn: () => api.listUsers() });
  const usersById = useMemo(() => {
    const map = new Map<string, { user_id: string; name: string; email: string }>();
    for (const u of usersQuery.data?.items ?? []) map.set(u.user_id, u);
    return map;
  }, [usersQuery.data]);

  const author = comment.created_by ? usersById.get(comment.created_by) : undefined;
  const authorName = author?.name ?? t('v2.detail.audit_author_unknown', 'unbekannt');
  const date = new Intl.DateTimeFormat(i18n.language, {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(comment.created_at));
  // `@[uuid]`-Tokens zu `@Name` auflösen — sonst stünde die rohe UUID in der Vorschau.
  // Ein String (nicht renderMentionBody), weil derselbe Text ins `title`-Attribut muss.
  const body = mentionBodyToPlainText(comment.body, usersById);

  return (
    <div
      className="flex items-baseline gap-3 mt-1.5 pl-5 min-w-0"
      title={t('v2.worklist.last_comment_tooltip', '{{author}} am {{date}}: {{body}}', {
        author: authorName,
        date,
        body,
      })}
    >
      <span aria-hidden="true" className="text-[11px] shrink-0" style={{ color: 'var(--ink-3)' }}>
        💬
      </span>
      <span className="sr-only">{t('v2.worklist.last_comment_label', 'Letzter Kommentar')}</span>
      <span className="text-[13px] truncate min-w-0 flex-1" style={{ color: 'var(--ink)' }}>
        {body}
      </span>
      <span
        className="text-[10.5px] shrink-0 hidden sm:inline"
        style={{ fontFamily: 'var(--f-mono)', color: 'var(--ink-3)' }}
      >
        {shortName(authorName)} · {date}
      </span>
    </div>
  );
}
